import { accountStatusMap, JobResponse, Jobs, NewOpportunity, OrderCardProps } from '../types'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import advanced from 'dayjs/plugin/advancedFormat'
import { tableStatusMappings, InvestmentStatus, WithdrawalStatus } from './message'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advanced)

// Safely coerce values to finite numbers to avoid NaN/Infinity in UI math.
export const toFiniteNumber = (value: any, fallback = 0): number => {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

export const concatClassName = (classNames?: string[]) => {
  // Join class names with a single space; return empty string when none.
  return classNames && classNames.length > 0 ? classNames.join(' ') : ''
}

export const createSlug = (slug: string) =>
  slug
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')

export const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/
export const numberRegex = /^[1-9]\d*(\.\d+)?$/
export const calculateRemainingDays = (startDate: Date, endDate: Date): number => {
  // const startDate = Date.now()
  if (startDate.getTime() < endDate.getTime())
    return Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
  else return 0
}

export const convertToNumber = (input: string): number => {
  // Extract numeric characters (digits and dot) and parse as float.
  let convertedToNumber = ''
  for (let i = 0; i < input.length; i++) {
    const hasNumber = /\d/
    if (hasNumber.test(input[i]) || input[i] === '.') {
      convertedToNumber += input[i]
    }
  }
  const num = parseFloat(convertedToNumber)
  return toFiniteNumber(num, 0)
}

export const convertToFloat = (value: string): number => {
  // Use numeric coercion and guard against NaN/Infinity
  return toFiniteNumber(Number(value), 0)
}

export const calculateCommittedPercentage = (
  committedAmount: string,
  raiseGoal?: string,
  assetClass?: string,
  assetClassData?: any,
): string => {
  try {
    let goal: number

    // For Listed Equity, use total_shares from asset_class_data
    if (assetClass === 'Listed Equity' && assetClassData?.total_shares) {
      goal = convertToNumber(assetClassData.total_shares.toString())
    } else {
      // For other asset classes, use raise_goal
      if (!raiseGoal || raiseGoal === '0' || raiseGoal === 'null') {
        return ''
      }
      goal = convertToNumber(raiseGoal.toString())
    }

    const amount = convertToNumber(committedAmount.toString())
    const remainingGoal = (amount * 100) / goal
    return remainingGoal.toFixed(2).toString().concat('%')
  } catch (err) {
    return '0%'
  }
}

export const calculatePercentage = (minValue: string, maxValue: string): number => {
  const min = convertToFloat(minValue)
  const max = convertToFloat(maxValue)
  if (max > 0) return (min * 100) / max
  return -1
}

export const isValidFileSize = (file: File, type: 'image' | 'logo' | 'doc' | 'video') => {
  // Size in KB
  const size = Math.round(file.size / 1024)
  const limitKB = 1 * 1024 * 10 // existing policy: 10 MB in KB
  return size <= limitKB
}

/** Exact byte limit used by Coming Soon cover uploads (10 MB). */
export const COMING_SOON_MAX_IMAGE_BYTES = 10 * 1024 * 1024

export const isValidImageType = (file: File) => {
  const extension = file.name.substr(file.name.lastIndexOf('.'))
  const allowedExtensionsRegx = /(\.jpg|\.jpeg)$/i
  return allowedExtensionsRegx.test(extension)
}

/** Company logos accept JPEG/JPG and PNG, so a logo can keep a transparent background. */
export const isValidLogoImageType = (file: File) => {
  const extension = file.name.substr(file.name.lastIndexOf('.'))
  const allowedExtensionsRegx = /(\.jpg|\.jpeg|\.png)$/i
  return allowedExtensionsRegx.test(extension)
}

/** Coming Soon covers accept JPEG/JPG and PNG (matches admin form copy). */
export const isValidComingSoonImageType = (file: File) => {
  const extension = file.name.substr(file.name.lastIndexOf('.'))
  const allowedExtensionsRegx = /(\.jpg|\.jpeg|\.png)$/i
  return allowedExtensionsRegx.test(extension)
}

/** True when the uploaded file's actual size is within `maxBytes` (inclusive). */
export const isWithinMaxFileBytes = (file: File, maxBytes: number) => file.size <= maxBytes

export const isValidDocType = (file: File) => {
  const extension = file.name.substr(file.name.lastIndexOf('.'))
  const allowedExtensionsRegx = /(\.docx|\.pdf)$/i
  return allowedExtensionsRegx.test(extension)
}

export const isValidVideoType = (file: File) => {
  const extension = file.name.substr(file.name.lastIndexOf('.'))
  const allowedExtensionsRegx = /(\.mp4)$/i
  return allowedExtensionsRegx.test(extension)
}

export const displayPercentage = (value: string): string => `${convertToFloat(value).toFixed(2)}%`

export const truncateNumber = (num: number, places = 2) =>
  Math.trunc(num * Math.pow(10, places)) / Math.pow(10, places)

export const displayDollar = (
  value: string | undefined,
  currencyCode: string | undefined,
  maximumFractionDigits = 2,
  forceStandardFormat = false,
  forceFractionDigits = false,
) => {
  const oneBillion = 1000000000
  const oneMillion = 1000000
  const oneThousand = 1000
  const dollar = value && parseFloat(value) / 100
  let output = 0

  if (Number.isInteger(Number(value)) && !forceFractionDigits) maximumFractionDigits = 0

  if (dollar && dollar >= oneBillion && !forceStandardFormat) {
    output = truncateNumber(dollar / oneBillion)
    return `${output.toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      maximumFractionDigits: maximumFractionDigits,
    })}b`
  } else if (dollar && dollar >= oneMillion && !forceStandardFormat) {
    output = truncateNumber(dollar / oneMillion)
    return `${output.toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      maximumFractionDigits: maximumFractionDigits,
    })}m`
  } else if (dollar && dollar >= oneThousand && !forceStandardFormat) {
    output = truncateNumber(dollar / oneThousand)
    return `${output.toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      maximumFractionDigits: maximumFractionDigits,
    })}k`
  }
  return dollar
    ? `${dollar.toLocaleString('en-US', {
        style: 'currency',
        currency: currencyCode || 'USD',
        maximumFractionDigits: maximumFractionDigits,
      })}`
    : '$0'
}

export const displayTicketSize = (value: string | undefined, currencyCode: string | undefined) => {
  if (!value) return '$0'

  const oneBillion = 1000000000
  const oneMillion = 1000000
  const oneThousand = 1000
  const dollar = parseFloat(value) / 100

  if (dollar >= oneBillion) {
    const output = Math.round((dollar / oneBillion) * 100) / 100
    const fractionDigits = output % 1 === 0 ? 0 : 2
    return `${output.toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })}b`
  } else if (dollar >= oneMillion) {
    const output = Math.round((dollar / oneMillion) * 100) / 100
    const fractionDigits = output % 1 === 0 ? 0 : 2
    return `${output.toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })}m`
  } else if (dollar >= oneThousand) {
    const output = Math.round((dollar / oneThousand) * 100) / 100
    const fractionDigits = output % 1 === 0 ? 0 : 2
    return `${output.toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })}k`
  }

  const fractionDigits = dollar % 1 === 0 ? 0 : 2
  return `${dollar.toLocaleString('en-US', {
    style: 'currency',
    currency: currencyCode || 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`
}

export const displayCurrency = (dollar: string, currencyCode = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: 2,
    notation: 'compact',
  }).format(convertToFloat(dollar))
}

// Helpers for cent-based amounts used across investor invest flow
export const formatCents = (cents: number | string | undefined | null): string => {
  const n = Number(cents ?? 0)
  if (!Number.isFinite(n)) return '0.00'
  return (n / 100).toFixed(2)
}

export const parseToCents = (value: string): number => {
  if (!value) return 0
  const cleaned = value.replace(/[^0-9.-]+/g, '')
  const num = parseFloat(cleaned)
  if (!Number.isFinite(num)) return 0
  return Math.round(num * 100)
}

/** Parse a user-entered decimal dollar amount (positive). */
export const parseDecimalAmount = (value: string): number | undefined => {
  if (!value?.trim()) return undefined
  const cleaned = value.replace(/[^0-9.-]+/g, '')
  const num = parseFloat(cleaned)
  if (!Number.isFinite(num) || num <= 0) return undefined
  return num
}

export const isPositiveDecimalAmount = (value: string): boolean =>
  parseDecimalAmount(value) !== undefined

/** Read a decimal dollar amount from API fields that may be camelCase or snake_case. */
export const getDecimalAmountField = (
  obj: object | null | undefined,
  camelKey: string,
  snakeKey: string,
): number | undefined => {
  if (!obj) return undefined
  const record = obj as Record<string, unknown>
  const val = record[camelKey] ?? record[snakeKey]
  if (val === null || val === undefined || val === '') return undefined
  const n = Number(val)
  return Number.isFinite(n) ? n : undefined
}

/** Format a cent-based amount as a decimal string for form pre-fill (e.g. 120000 → "1200.00"). */
export const formatCentsAsDecimalString = (cents: number | string | undefined | null): string =>
  formatCents(cents)

export const displayDecimalDollar = (
  value: number | string | undefined | null,
  currencyCode: string | undefined,
): string => {
  const n = Number(value ?? 0)
  if (!Number.isFinite(n)) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(0)
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode || 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n)
}

/** Read a cent amount from API fields that may be camelCase or snake_case. */
export const getCentsField = (
  obj: object | null | undefined,
  camelKey: string,
  snakeKey: string,
): number | undefined => {
  if (!obj) return undefined
  const record = obj as Record<string, unknown>
  const val = record[camelKey] ?? record[snakeKey]
  if (val === null || val === undefined || val === '') return undefined
  const n = Number(val)
  return Number.isFinite(n) ? n : undefined
}

const ORDER_STATUS_ERROR_MESSAGES: Record<string, string> = {
  'investment-not-found': 'Investment for the opportunity not found',
  'withdrawal-not-found': 'Redemption for the opportunity has not been found',
  'invalid-status-transition': 'This status change is not allowed from the current state',
  'invalid-actual-amount': 'Please enter a valid amount',
  'actual-amount-required': 'Actual amount is required for this status update',
  'actual-amount-received-required':
    'Actual amount received is required when marking payment received',
  'actual-amount-sent-required':
    'Actual amount sent is required when marking trade executed after payment was recorded',
  'payment-not-recorded': 'Payment must be recorded before executing the trade',
  'rate-of-conversion-required':
    'Rate of conversion is required when reference valuation currency differs from opportunity currency',
  'insufficient-remaining-quantity': 'Redemption quantity exceeds available units',
  'withdrawal-quantity-exceeds-investment': 'Redemption quantity exceeds available units',
  'withdrawal-pricing-unavailable':
    'Unit price (NAV) is missing. Enter units manually, or add a unit price and retry.',
}

export type ReferenceValuation = {
  currency: string
  amount: number
}

/** Resolve opportunity reference valuation from investment detail / project payloads. */
export const getReferenceValuation = (
  investmentDetail?: Record<string, any> | null,
  project?: Record<string, any> | null,
): ReferenceValuation | undefined => {
  const candidates = [
    project?.reference_valuation,
    investmentDetail?.reference_valuation,
    investmentDetail?.opportunity_metadata?.reference_valuation,
    investmentDetail?.opportunity?.metadata?.reference_valuation,
  ]

  for (const candidate of candidates) {
    if (!candidate) continue
    const currency =
      typeof candidate.currency === 'string'
        ? candidate.currency
        : typeof candidate.currency_code === 'string'
        ? candidate.currency_code
        : ''
    const amount = Number(candidate.amount)
    if (currency && Number.isFinite(amount) && amount > 0) {
      return { currency, amount }
    }
  }
  return undefined
}

export const needsRateOfConversion = (
  referenceValuation: ReferenceValuation | undefined,
  opportunityCurrency: string | undefined,
): boolean => {
  if (!referenceValuation || !opportunityCurrency) return false
  return referenceValuation.currency !== opportunityCurrency
}

/**
 * Preview: investment amount ÷ (reference valuation ÷ rate) × 100
 * Amounts are in cents; rate is valuation-currency units per 1 opportunity-currency unit.
 */
export const previewUnderlyingExposurePercent = (
  investmentAmountCents: number,
  referenceValuationCents: number,
  rate: number,
): number | undefined => {
  if (
    !Number.isFinite(investmentAmountCents) ||
    !Number.isFinite(referenceValuationCents) ||
    !Number.isFinite(rate) ||
    investmentAmountCents <= 0 ||
    referenceValuationCents <= 0 ||
    rate <= 0
  ) {
    return undefined
  }
  return (investmentAmountCents / (referenceValuationCents / rate)) * 100
}

export const getOrderStatusErrorMessage = (
  response: { data?: { code?: string; message?: string } } | undefined,
  fallback: string,
): string => {
  const code = response?.data?.code
  if (code && ORDER_STATUS_ERROR_MESSAGES[code]) {
    return ORDER_STATUS_ERROR_MESSAGES[code]
  }
  return response?.data?.message || fallback
}

export const getAllowedInvestmentStatusOptions = (currentStatus: string) => {
  const map: Record<string, string[]> = {
    // Wallet flow: INITIATED -> PAYMENT_DONE -> ORDER_PLACED are client-driven; admin only executes or rejects.
    INITIATED: [],
    PAYMENT_DONE: [],
    ORDER_PLACED: ['TRADE_EXECUTED', 'REJECTED'],
    REINVESTMENT_PENDING: ['TRADE_EXECUTED', 'REJECTED', 'CANCELLED'],
    TRADE_EXECUTED: [],
    REFUND_PROCESSED: [],
    REJECTED: [],
    CANCELLED: [],
  }

  const allowed = map[currentStatus] ?? []
  return InvestmentStatus.filter(
    (item) => allowed.includes(item.value) || item.value === currentStatus,
  ).map((option) => (option.value === currentStatus ? { ...option, disabled: true } : option))
}

export const getAllowedWithdrawalStatusOptions = (currentStatus: string) => {
  const map: Record<string, string[]> = {
    // Investor-only from INITIATED: ORDER_PLACED, CANCELLED (24h window)
    INITIATED: [],
    ORDER_PLACED: ['TRADE_EXECUTED', 'REJECTED', 'CANCELLED'],
    TRADE_EXECUTED: [],
    REJECTED: [],
    CANCELLED: [],
  }

  const allowed = map[currentStatus] ?? []
  const options = WithdrawalStatus.filter(
    (item) => allowed.includes(item.value) || item.value === currentStatus,
  ).sort((a, b) => {
    if (a.value === currentStatus) return 1
    if (b.value === currentStatus) return -1
    return allowed.indexOf(a.value) - allowed.indexOf(b.value)
  })
  return options.map((option) =>
    option.value === currentStatus ? { ...option, disabled: true } : option,
  )
}

export const getFileKeyFromSignedUrl = (signedUrl: string): string => {
  if (signedUrl.startsWith('http')) {
    const regexToExtractKey = /^.*\/([^/]+?)\?.*$/
    const match = signedUrl.match(regexToExtractKey)
    return match && match[1] ? match[1] : signedUrl
  }
  return signedUrl
}

export const sanitizeSignedUrlsForObject = (keyNames: string[], data: object): object => {
  const updated: any = data
  Object.keys(data).map((key) => {
    if (keyNames.indexOf(key) !== -1) {
      updated[key] = getFileKeyFromSignedUrl(updated[key])
    }
  })
  return updated
}

export const removeEmptyValueFromObject = (source: any) => {
  Object.keys(source).forEach((key) => {
    if (!source[key] || source[key] === '') delete source[key]
  })
  return source
}

export const formatPercentage = (percentage: any, decimalPlaces = 2) => {
  try {
    const fixed = parseFloat(percentage).toFixed(decimalPlaces)
    return `${fixed}%`
  } catch (err) {
    return '0%'
  }
}

export const trimText = (content: string, charCount = 100) => {
  if (!content) return ''
  return content.length < charCount ? content : content.substring(0, charCount).concat('...')
}

export const formatStatus = (status: string) => {
  if (!status) return ''
  const lower = status.toLowerCase()
  const capitalize = lower[0].toUpperCase() + lower.slice(1)
  return capitalize.replace(/_/g, ' ').replace(/-/g, ' ')
}

export const filterJobFromJobResp = (response: JobResponse[]): Jobs[] => {
  const nested = response.map((resp) => {
    return resp.payload.fileIds.map((fileId, index): Jobs => {
      const jobResult =
        resp.result && resp.result.jobResults && resp.result.jobResults.length > 0
          ? resp.result.jobResults[index]
          : undefined

      let status = 'processing'
      if (jobResult && jobResult.status) {
        status = jobResult.status === 'failed' ? jobResult.reason : jobResult.status
      }

      return {
        jobId: resp.jobId,
        createdAt: resp.createdAt,
        fileId: fileId,
        status: status,
        email: jobResult ? jobResult.email : '',
        investmentId: jobResult && jobResult.metadata ? jobResult.metadata.id || '' : '',
      }
    })
  })
  return nested.flat()
}

export const isMultiple = (x: number, y: number) => (x % y === 0 ? true : false)

export const convertNumberWithCommas = (input: string | number): string => {
  const val = typeof input === 'string' ? convertToNumber(input) : input
  return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const convertDateToDayjs = (date: Date | undefined | null) => {
  return dayjs(date)
}

export const sanitizeOpportunityPayload = (
  payload: NewOpportunity,
  fieldsToRemove: string[],
): NewOpportunity => {
  const sanitizedPayload = { ...payload }

  fieldsToRemove.forEach((field: string) => {
    delete (sanitizedPayload as any)[field]
  })

  // For sanitizing fields like 'Highlights', 'Images', 'Key Factors'
  Object.keys(sanitizedPayload).forEach((key) => {
    if (Array.isArray(sanitizedPayload[key])) {
      sanitizedPayload[key] = (sanitizedPayload[key] as []).filter(
        (item: any) => item !== null && item !== undefined && item !== '',
      )
    }
  })

  // For sanitizing 'Documents' field
  if ('documents' in sanitizedPayload && sanitizedPayload.documents.length > 0) {
    sanitizedPayload.documents = sanitizedPayload.documents.filter(
      (item: any) => item.url.trim() !== '' && item.title.trim() !== '',
    )
  }

  return sanitizedPayload
}

export const getFormattedDate = (date: Date, format: string): any => {
  return dayjs(date).format(format)
}

export const getDormantCountDownString = (approvalDate: string | Date): string => {
  const startDate = new Date(approvalDate)
  const dormantDate = new Date(startDate)
  dormantDate.setMonth(dormantDate.getMonth() + 6)

  const currentDate = new Date()

  return getPeriodDifferenceString(currentDate, dormantDate)
}

export const getPeriodDifferenceString = (start: string | Date, end: string | Date): string => {
  const startDate = new Date(start)
  const targetDate = new Date(end)

  // Calculate the difference in milliseconds
  const diffTime = targetDate.getTime() - startDate.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  // If less than 1 day
  if (diffDays < 1) {
    return '< 1 day'
  }

  // If less than 7 days, return days
  if (diffDays <= 7) {
    if (diffDays === 7) {
      return '1+ week left'
    }
    if (diffDays === 1) {
      return '1 day left'
    }
    return `${diffDays}+ days left`
  }

  // Calculate weeks
  const diffWeeks = Math.floor(diffDays / 7)
  if (diffWeeks <= 4) {
    if (diffWeeks === 4) {
      return '4+ weeks left'
    }
    if (diffWeeks === 1) {
      return '1 week left'
    }
    return `${diffWeeks}+ weeks left`
  }

  // Calculate months
  const diffMonths = Math.floor(diffDays / 31)
  if (diffMonths <= 6) {
    if (diffMonths === 6) {
      return '6 months left'
    }
    if (diffMonths === 1) {
      return '1 month left'
    }
    return `${diffMonths}+ months left`
  }

  return '6 months left'
}

export const getTableClassNames = ({
  investor_account_status,
  investment_completed,
  completed_steps,
  payment_copy = '',
  transaction_reference_number = '',
  order_status,
  order_type,
  withdrawal_completed,
}: Partial<OrderCardProps>) => {
  if (investor_account_status === accountStatusMap.DORMANT) {
    return tableStatusMappings.dormant.class
  }

  if (order_status === 'CANCELLED') {
    return tableStatusMappings['cancelled-order'].class
  }

  if (order_status === 'REJECTED') {
    return tableStatusMappings['rejected-order'].class
  }

  if (order_status === 'REFUND_PROCESSED') {
    return tableStatusMappings['refunded-order'].class
  }

  if (
    order_type === 'BUY' &&
    investment_completed !== undefined &&
    investment_completed === false
  ) {
    if (completed_steps === '2' && !payment_copy && !transaction_reference_number) {
      return tableStatusMappings['incomplete-order-3'].class
    }

    return tableStatusMappings['incomplete-order'].class
  }
  if (
    order_type === 'SELL' &&
    withdrawal_completed !== undefined &&
    withdrawal_completed === false
  ) {
    return tableStatusMappings['incomplete-order'].class
  }

  return ''
}

export const getToolTipText = ({
  investor_account_status,
  investment_completed,
  completed_steps,
  payment_copy = '',
  transaction_reference_number = '',
  order_status = '',
  order_type,
  withdrawal_completed,
}: Partial<OrderCardProps>) => {
  if (investor_account_status === accountStatusMap.DORMANT) {
    return tableStatusMappings.dormant.tooltipText
  }

  if (order_status === 'CANCELLED') {
    return tableStatusMappings['cancelled-order'].tooltipText
  }

  if (order_status === 'REJECTED') {
    return tableStatusMappings['rejected-order'].tooltipText
  }

  if (order_status === 'REFUND_PROCESSED') {
    return tableStatusMappings['refunded-order'].tooltipText
  }

  if (
    order_type === 'BUY' &&
    investment_completed !== undefined &&
    investment_completed === false
  ) {
    if (completed_steps === '2' && !payment_copy && !transaction_reference_number) {
      return tableStatusMappings['incomplete-order-3'].tooltipText
    }
    return tableStatusMappings['incomplete-order'].tooltipText
  }

  if (
    order_type === 'SELL' &&
    withdrawal_completed !== undefined &&
    withdrawal_completed === false
  ) {
    return tableStatusMappings['incomplete-order'].tooltipText
  }

  return ''
}

export const getOrdinalSuffix = (day: number) => {
  if (day > 3 && day < 21) return 'th'
  switch (day % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

export const getTimestampWithTimezone = (date: Date, format: string): string => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const formattedTime = dayjs(date).tz(timeZone).format(format)

  // Replace full timezone names with abbreviations
  return formattedTime
    .replace('India Standard Time', 'IST')
    .replace('Coordinated Universal Time', 'UTC')
    .replace('Eastern Standard Time', 'EST')
    .replace('Eastern Daylight Time', 'EDT')
    .replace('Pacific Standard Time', 'PST')
    .replace('Pacific Daylight Time', 'PDT')
    .replace('Central Standard Time', 'CST')
    .replace('Central Daylight Time', 'CDT')
    .replace('Mountain Standard Time', 'MST')
    .replace('Mountain Daylight Time', 'MDT')
}

export const formatSnakeCaseToLabel = (key: string): string => {
  if (!key) return ''
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // capitalize each part
    .join(' ')
}
